import {
  Stack,
  styled,
  Switch,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Grid,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  IDataSharingSettingsDTO,
  IDataSharingSettingsJobDTO,
  getDataSharingSettings,
  updateDataSharingSettings,
  ITeam,
  getAllWorkspaceTeams,
  getJobsDataSharingSettings,
  updateJobsDataSharingSettings,
} from 'wisestep-api';
import { Spinner, Item, WsButton, WsColors, WsTypoColors, WsTypography } from 'wisestep-base';

import { useWorkspaceContext } from '../../context';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../hooks/useAtsCustomAPIAccess';
import { success, warning } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import AppSettingsLayout from '../appsettings/AppSettingsLayout';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../teams/helper/defaultValues';
const StyledStack = styled(Stack)({
  padding: '2',
  backgroundColor: WsColors.TheBlues50,
  gap: 3,
});
const DataSharing = () => {
  const [teamsData, setTeamsData] = useState<IDataSharingSettingsDTO>();
  const [teamsJobData, setTeamsJobData] = useState<IDataSharingSettingsJobDTO>();
  const [teams, setTeams] = useState<ITeam[]>([]);
  const [loadPage, setLoadPage] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_DATA_SHARING_SETTINGS,
  ]);
  const workspaceContext = useWorkspaceContext();
  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  useEffect(() => {
    (async function () {
      const getDataSharingSettingsRes = await getDataSharingSettings();
      const getDataSharingSettingsData: IDataSharingSettingsDTO = getDataSharingSettingsRes.data;
      setTeamsData(getDataSharingSettingsData);

      const getDataSharingSettingsJobsRes = await getJobsDataSharingSettings();
      const getDataSharingSettingsJobData: IDataSharingSettingsJobDTO =
        getDataSharingSettingsJobsRes.data;
      setTeamsJobData(getDataSharingSettingsJobData);

      const getAllTeamsRes = await getAllWorkspaceTeams();
      setTeams(getAllTeamsRes.data);
      setLoadPage(true);
    })();
  }, []);

  const addToSharing = (checked, teamId, sharedAsset) => {
    if (teamsData && (sharedAsset === 'Submissions' || sharedAsset === 'Mappings')) {
      const tempTeamsData: IDataSharingSettingsDTO = { ...teamsData };
      if (sharedAsset === 'Submissions') {
        tempTeamsData.teamsSharingSubmissions = checked
          ? [...tempTeamsData.teamsSharingSubmissions, teamId]
          : tempTeamsData?.teamsSharingSubmissions.filter((id) => id !== teamId);
      } else {
        tempTeamsData.teamsSharingMappings = checked
          ? [...tempTeamsData.teamsSharingMappings, teamId]
          : tempTeamsData?.teamsSharingMappings.filter((id) => id !== teamId);
      }
      setTeamsData(tempTeamsData);
    } else if (teamsJobData && sharedAsset === 'Jobs') {
      const tempTeamsJobData: IDataSharingSettingsJobDTO = { ...teamsJobData };
      tempTeamsJobData.teamsSharingJobs = checked
        ? [...tempTeamsJobData.teamsSharingJobs, teamId]
        : tempTeamsJobData?.teamsSharingJobs.filter((id) => id !== teamId);
      setTeamsJobData(tempTeamsJobData);
    }
    setLoadPage(true);
  };

  const onEnableFullAccess = (checked, sharedAsset) => {
    if (teamsData && sharedAsset === 'Candidate') {
      const tempTeamsData: IDataSharingSettingsDTO = { ...teamsData };
      tempTeamsData.enableFullAccess = !checked;
      setTeamsData(tempTeamsData);
    } else if (teamsJobData && sharedAsset === 'Jobs') {
      const tempTeamsJobData: IDataSharingSettingsJobDTO = { ...teamsJobData };
      tempTeamsJobData.enableFullAccess = !checked;
      setTeamsJobData(tempTeamsJobData);
    }
  };

  const onSave = async () => {
    try {
      teamsData && (await updateDataSharingSettings(teamsData));
      teamsJobData && (await updateJobsDataSharingSettings(teamsJobData));
      setNotification(success('Data Sharing Settings Updated Successfully.'));
      window.location.reload();
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  const dataSharingOptions = [
    {
      label: 'Data Sharing',
      url: '/data-sharing',
    },
  ];
  return (
    <AppSettingsLayout
      title="Data Sharing"
      breadCrumnbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.TEAMS_AND_USERS,
        Pages['DATA_SHARING']
      )}
      options={dataSharingOptions}
    >
      {loadPage ? (
        <>
          <StyledStack direction={'row'} justifyContent={'space-between'}>
            <Item>
              <WsTypography color={WsTypoColors.SECONDARY}>Data Sharing</WsTypography>
            </Item>
            {manageAccess && (
              <Item>
                <WsButton onClick={onSave} variant="contained" size="medium">
                  Save
                </WsButton>
              </Item>
            )}
          </StyledStack>
          <Grid container direction="row" justifyContent="flex-start" p={1}>
            <Grid item>
              <WsTypography color={WsTypoColors.PRIMARY}>
                Enable Custom Rules for Jobs:
              </WsTypography>
            </Grid>
            <Grid item pl={5.5}>
              <Switch
                disabled={!manageAccess}
                checked={!teamsJobData?.enableFullAccess}
                onChange={(ev) => onEnableFullAccess(ev.target.checked, 'Jobs')}
              />
            </Grid>
          </Grid>
          <Grid container direction="row" justifyContent="flex-start" p={1} pt={0}>
            <Grid item>
              <WsTypography color={WsTypoColors.PRIMARY}>
                Enable Custom Rules for Candidate:
              </WsTypography>
            </Grid>
            <Grid item pl={1}>
              <Switch
                disabled={!manageAccess}
                checked={!teamsData?.enableFullAccess}
                onChange={(ev) => onEnableFullAccess(ev.target.checked, 'Candidate')}
              />
            </Grid>
          </Grid>
          <TableContainer component={Paper}>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell align="center" colSpan={2}>
                    Jobs
                  </TableCell>
                  <TableCell align="center" colSpan={2}>
                    Pipeline
                  </TableCell>
                  <TableCell align="center" colSpan={2}>
                    Submissions
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Teams</TableCell>
                  <TableCell>My Teams</TableCell>
                  <TableCell>All Teams</TableCell>
                  <TableCell>My Teams</TableCell>
                  <TableCell>All Teams</TableCell>
                  <TableCell>My Teams</TableCell>
                  <TableCell>All Teams</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teams.map((team, index) => (
                  <TableRow key={index}>
                    <TableCell>{team?.name}</TableCell>
                    <TableCell>
                      <WsTypography cursor="pointer" hoverText="Cannot edit this access">
                        <Switch disabled defaultChecked color="info" />
                      </WsTypography>
                    </TableCell>
                    <TableCell>
                      {teamsJobData?.enableFullAccess ? (
                        <WsTypography cursor="pointer" hoverText="Cannot edit this access">
                          <Switch
                            defaultChecked={teamsJobData?.enableFullAccess}
                            disabled
                            color="info"
                          />
                        </WsTypography>
                      ) : (
                        <Switch
                          disabled={!manageAccess}
                          checked={teamsJobData?.teamsSharingJobs?.some((jobId) =>
                            jobId === team?.id ? true : false
                          )}
                          onChange={(ev) => addToSharing(ev.target.checked, team?.id, 'Jobs')}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <WsTypography cursor="pointer" hoverText="Cannot edit this access">
                        <Switch disabled defaultChecked color="info" />
                      </WsTypography>
                    </TableCell>
                    <TableCell>
                      {teamsData?.enableFullAccess ? (
                        <WsTypography cursor="pointer" hoverText="Cannot edit this access">
                          <Switch
                            defaultChecked={teamsData?.enableFullAccess}
                            disabled
                            color="info"
                          />
                        </WsTypography>
                      ) : (
                        <Switch
                          disabled={!manageAccess}
                          checked={teamsData?.teamsSharingMappings?.some((mappingId) =>
                            mappingId === team?.id ? true : false
                          )}
                          onChange={(ev) => addToSharing(ev.target.checked, team?.id, 'Mappings')}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <WsTypography cursor="pointer" hoverText="Cannot edit this access ">
                        <Switch disabled defaultChecked color="info" />
                      </WsTypography>
                    </TableCell>
                    <TableCell>
                      {teamsData?.enableFullAccess ? (
                        <WsTypography cursor="pointer" hoverText="Cannot edit this access">
                          <Switch
                            defaultChecked={teamsData?.enableFullAccess}
                            disabled
                            color="info"
                          />
                        </WsTypography>
                      ) : (
                        <Switch
                          disabled={!manageAccess}
                          checked={teamsData?.teamsSharingSubmissions?.some((submissionsId) =>
                            submissionsId === team?.id ? true : false
                          )}
                          onChange={(ev) =>
                            addToSharing(ev.target.checked, team?.id, 'Submissions')
                          }
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Spinner />
      )}
    </AppSettingsLayout>
  );
};

export default DataSharing;
